
import Vue from 'vue';
import Component, { mixins } from 'vue-class-component';
import { ValidationProvider, ValidationObserver } from 'vee-validate';
import { State, Action } from 'vuex-class';
import { Prop, Watch } from 'vue-property-decorator';
import { RootState } from '@/store/store';
import {
  Citation,
  CreateCitationRequestPayload,
  EditCitationRequestPayload
} from '@/store/modules/documents/types/citations.types';
import { RemoveRevisionCitationRequestPayload } from '@/store/modules/documents/types/documents.types';
import { cloneDeep as _cloneDeep, get as _get } from 'lodash';
import { ProjectsApiState } from '@/store/modules/projects/types/projects.types';
import { isDifferent, isTruthy } from '@/jbi-shared/util/watcher.vue-decorator';
import {
  CitationTypes,
  CitationAuthor,
  IndividualCitationAuthor,
  OrganisationCitationAuthor
} from '@/jbi-shared/types/citation.fields';
import { GetRevisionCitationsRequestPayload } from '../../../../../store/modules/documents/types/documents.types';
import { EditorComputedValuesMixin } from '../../../mixins/editor-computed-values.mixin';
import {
  updateCitationInOps,
  updateCitationInOpsInStagingEditor
} from '../../../../../utils/editor.util';
import { ToastProgrammatic as Toast } from 'buefy';
import { StagingEditorComputedValuesMixin } from '../../../../StagingArea/mixins/staging-editor-computed-values.mixin';
import { isInteger } from 'lodash';
import CitationAuthors from './CitationAuthors.vue';
import { CitationAuthorFormItem } from '@/types/CitationAuthor';

// Write all the constants here which we use and may need to change in future
const AUTHOR_TYPES = {
  ORGANISATION: 'Organisation',
  INDIVIDUAL: 'Individual'
} as const;

const FORM_FIELD_NAMES = {
  AUTHOR: 'author',
  TITLE: 'title'
} as const;

export interface FormFieldsProperties {
  type: string;
  name: string;
  rules: string;
  value: string;
  label: string;
  placeHolder: string;
}

export interface FormFields {
  types: {
    Book: FormFieldsProperties[];
    Generic: FormFieldsProperties[];
    'Book Section': FormFieldsProperties[];
    'Conference Paper': FormFieldsProperties[];
    'Journal Article': FormFieldsProperties[];
    Report: FormFieldsProperties[];
    WebPage: FormFieldsProperties[];
    Plain: FormFieldsProperties[];
  };
}

// tslint:disable:object-literal-key-quotes
const allFormFields: FormFields = {
  types: {
    Book: [
      {
        type: 'text',
        name: 'author',
        rules: '',
        value: '',
        label: 'Author(s)',
        placeHolder: ''
      },
      {
        type: 'text',
        name: 'title',
        rules: 'required',
        value: '',
        label: 'Title',
        placeHolder: ''
      },
      {
        type: 'text',
        name: 'edition',
        rules: '',
        value: '',
        label: 'Edition',
        placeHolder: ''
      },
      {
        type: 'text',
        name: 'seriesEditor',
        rules: '',
        value: '',
        label: 'Series Editor',
        placeHolder: ''
      },
      {
        type: 'text',
        name: 'publisherPlace',
        rules: '',
        value: '',
        label: 'Publisher Place',
        placeHolder: ''
      },
      {
        type: 'text',
        name: 'publisher',
        rules: '',
        value: '',
        label: 'Publisher',
        placeHolder: ''
      },
      {
        type: 'text',
        name: 'issued',
        rules: '',
        value: '',
        label: 'Issue Date',
        placeHolder: ''
      },
      {
        type: 'text',
        name: 'numberOfPages',
        rules: '',
        value: '',
        label: 'Number of Pages',
        placeHolder: ''
      }
    ],
    Generic: [
      {
        type: 'text',
        name: 'author',
        rules: '',
        value: '',
        label: 'Author(s)',
        placeHolder: ''
      },
      {
        type: 'text',
        name: 'title',
        rules: 'required',
        value: '',
        label: 'Title',
        placeHolder: ''
      },
      {
        type: 'text',
        name: 'edition',
        rules: '',
        value: '',
        label: 'Edition',
        placeHolder: ''
      },
      {
        type: 'text',
        name: 'publisherPlace',
        rules: '',
        value: '',
        label: 'Publisher Place',
        placeHolder: ''
      },
      {
        type: 'text',
        name: 'publisher',
        rules: 'required',
        value: '',
        label: 'Publisher',
        placeHolder: ''
      },
      {
        type: 'text',
        name: 'issued',
        rules: 'required',
        value: '',
        label: 'Issue Date',
        placeHolder: ''
      },
      {
        type: 'text',
        name: 'accessed',
        rules: '',
        value: '',
        label: 'Accessed Date',
        placeHolder: ''
      },
      {
        type: 'text',
        name: 'pages',
        rules: '',
        value: '',
        label: 'Pages',
        placeHolder: ''
      },
      {
        type: 'text',
        name: 'url',
        rules: 'required',
        value: '',
        label: 'URL',
        placeHolder: ''
      }
    ],
    'Book Section': [
      {
        type: 'text',
        name: 'author',
        rules: '',
        value: '',
        label: 'Author(s)',
        placeHolder: ''
      },
      {
        type: 'text',
        name: 'bookSectionTitle',
        rules: 'required',
        value: '',
        label: 'Book Section Title',
        placeHolder: ''
      },
      {
        type: 'text',
        name: 'title',
        rules: '',
        value: '',
        label: 'Book Title',
        placeHolder: ''
      },
      {
        type: 'text',
        name: 'editors',
        rules: '',
        value: '',
        label: 'Editors',
        placeHolder: ''
      },
      {
        type: 'text',
        name: 'volume',
        rules: '',
        value: '',
        label: 'Volume',
        placeHolder: ''
      },
      {
        type: 'text',
        name: 'edition',
        rules: '',
        value: '',
        label: 'Edition',
        placeHolder: ''
      },
      {
        type: 'text',
        name: 'publisherPlace',
        rules: '',
        value: '',
        label: 'Publisher Place',
        placeHolder: ''
      },
      {
        type: 'text',
        name: 'publisher',
        rules: '',
        value: '',
        label: 'Publisher',
        placeHolder: ''
      },
      {
        type: 'text',
        name: 'issued',
        rules: '',
        value: '',
        label: 'Issue Date',
        placeHolder: 'eg. 03/04/2004'
      },
      {
        type: 'text',
        name: 'page',
        rules: '',
        value: '',
        label: 'Page',
        placeHolder: ''
      }
    ],
    'Conference Paper': [
      {
        type: 'text',
        name: 'author',
        rules: '',
        value: '',
        label: 'Author(s)',
        placeHolder: ''
      },
      {
        type: 'text',
        name: 'title',
        rules: 'required',
        value: '',
        label: 'Title',
        placeHolder: ''
      },
      {
        type: 'text',
        name: 'editors',
        rules: '',
        value: '',
        label: 'Editor(s)',
        placeHolder: ''
      },
      {
        type: 'text',
        name: 'eventName',
        rules: '',
        value: '',
        label: 'Event Name',
        placeHolder: ''
      },
      {
        type: 'text',
        name: 'eventPlace',
        rules: '',
        value: '',
        label: 'Event Place',
        placeHolder: ''
      },
      {
        type: 'text',
        name: 'issued',
        rules: '',
        value: '',
        label: 'Issue Date',
        placeHolder: ''
      },
      {
        type: 'text',
        name: 'publisherPlace',
        rules: '',
        value: '',
        label: 'Publisher Place',
        placeHolder: ''
      },
      {
        type: 'text',
        name: 'publisherName',
        rules: '',
        value: '',
        label: 'Publisher Name',
        placeHolder: ''
      },
      {
        type: 'text',
        name: 'page',
        rules: '',
        value: '',
        label: 'Page',
        placeHolder: ''
      }
    ],
    'Journal Article': [
      {
        type: 'text',
        name: 'author',
        rules: '',
        value: '',
        label: 'Author(s)',
        placeHolder: ''
      },
      {
        type: 'number',
        name: 'issued',
        rules: 'required',
        value: '',
        label: 'Year',
        placeHolder: ''
      },
      {
        type: 'text',
        name: 'title',
        rules: 'required',
        value: '',
        label: 'Title',
        placeHolder: ''
      },
      {
        type: 'text',
        name: 'container-title',
        rules: 'required',
        value: '',
        label: 'Journal Name',
        placeHolder: ''
      },
      {
        type: 'text',
        name: 'container-title-short',
        rules: '',
        value: '',
        label: 'Abbreviated Journal Name',
        placeHolder: ''
      },
      {
        type: 'text',
        name: 'volume',
        rules: '',
        value: '',
        label: 'Volume',
        placeHolder: ''
      },
      {
        type: 'text',
        name: 'issue',
        rules: '',
        value: '',
        label: 'Issue',
        placeHolder: ''
      },
      {
        type: 'text',
        name: 'page',
        rules: '',
        value: '',
        label: 'Pages',
        placeHolder: ''
      },
      {
        type: 'text',
        name: 'DOI',
        rules: 'required',
        value: '',
        label: 'DOI',
        placeHolder: ''
      }
    ],
    Report: [
      {
        type: 'text',
        name: 'author',
        rules: '',
        value: '',
        label: 'Author(s)',
        placeHolder: ''
      },
      {
        type: 'text',
        name: 'title',
        rules: 'required',
        value: '',
        label: 'Title',
        placeHolder: ''
      },
      {
        type: 'text',
        name: 'genre',
        rules: '',
        value: '',
        label: 'Genre',
        placeHolder: ''
      },
      {
        type: 'text',
        name: 'publisherPlace',
        rules: '',
        value: '',
        label: 'Publisher Place',
        placeHolder: ''
      },
      {
        type: 'text',
        name: 'publisher',
        rules: '',
        value: '',
        label: 'Publisher',
        placeHolder: ''
      },
      {
        type: 'text',
        name: 'issued',
        rules: '',
        value: '',
        label: 'Issue Date',
        placeHolder: ''
      },
      {
        type: 'text',
        name: 'reportNumber',
        rules: '',
        value: '',
        label: 'Report Number',
        placeHolder: ''
      },
      {
        type: 'text',
        name: 'documentNumber',
        rules: '',
        value: '',
        label: 'Document Number',
        placeHolder: ''
      }
    ],
    WebPage: [
      {
        type: 'text',
        name: 'author',
        rules: '',
        value: '',
        label: 'Author(s)',
        placeHolder: ''
      },
      {
        type: 'text',
        name: 'title',
        rules: 'required',
        value: '',
        label: 'Title',
        placeHolder: ''
      },
      {
        type: 'text',
        name: 'typeOfMedium',
        rules: '',
        value: '',
        label: 'Type Of Medium',
        placeHolder: ''
      },
      {
        type: 'text',
        name: 'publisherPlace',
        rules: '',
        value: '',
        label: 'Publisher Place',
        placeHolder: ''
      },
      {
        type: 'text',
        name: 'publisher',
        rules: '',
        value: '',
        label: 'Publisher',
        placeHolder: ''
      },
      {
        type: 'text',
        name: 'issued',
        rules: '',
        value: '',
        label: 'Issue Date',
        placeHolder: ''
      },
      {
        type: 'text',
        name: 'accessed',
        rules: '',
        value: '',
        label: 'Accessed Date',
        placeHolder: ''
      },
      {
        type: 'text',
        name: 'edition',
        rules: '',
        value: '',
        label: 'Edition',
        placeHolder: ''
      },
      {
        type: 'text',
        name: 'note',
        rules: '',
        value: '',
        label: 'Note',
        placeHolder: ''
      },
      {
        type: 'text',
        name: 'url',
        rules: '',
        value: '',
        label: 'URL',
        placeHolder: ''
      }
    ],
    Plain: [
      {
        type: 'textarea',
        name: 'plainText',
        rules: 'required',
        value: '',
        label: 'Plain Text',
        placeHolder: ''
      }
    ]
  }
};

type UserCitationTypes =
  | 'Book'
  | 'Generic'
  | 'Book Section'
  | 'Conference Paper'
  | 'Journal Article'
  | 'Report'
  | 'WebPage'
  | 'Plain';

const citationTypes: UserCitationTypes[] = Object.keys(
  allFormFields.types
) as UserCitationTypes[];

function getDefaultAuthor(): CitationAuthorFormItem {
  //default author type is individual
  return {
    id: Date.now(),
    literal: '',
    given: '',
    family: '',
    type: AUTHOR_TYPES.INDIVIDUAL
  };
}

@Component({
  components: { ValidationProvider, ValidationObserver, CitationAuthors }
})
export default class StagingCitationForm extends mixins(
  StagingEditorComputedValuesMixin
) {
  @Prop(Object) public citation!: Citation;

  @Prop() public referencedCitationIds!: number[];

  @Prop({ default: false }) public isPendingCitation!: boolean;

  public selectedType: UserCitationTypes | null = 'Journal Article';

  public formFields: FormFieldsProperties[] = [];

  public authors: CitationAuthorFormItem[] = [getDefaultAuthor()]; // Will be properly initialized in transformCitationToField()

  updateFormAuthors(authors: CitationAuthorFormItem[]) {
    // Use Vue's reactivity system to ensure proper updates
    this.$set(this, 'authors', authors);

    const authorFormField = this.formFields.find(
      (field) => field.name === FORM_FIELD_NAMES.AUTHOR
    );

    if (authorFormField) {
      authorFormField.value = JSON.stringify(authors);
    }
  }

  handleAddAuthor() {
    this.authors.push(getDefaultAuthor());

    // no need to update the form here. modifying the
    // new item will trigger updateForm
  }

  handleRemoveAuthor(authorId: number) {
    const newAuthors = this.authors.filter((author) => author.id !== authorId);

    this.updateFormAuthors(newAuthors);
  }

  handleUpdateAuthor(updatedAuthor: CitationAuthorFormItem) {
    const newAuthors = this.authors.map((author) =>
      author.id === updatedAuthor.id ? updatedAuthor : author
    );

    this.updateFormAuthors(newAuthors);
  }

  get citationTypes(): UserCitationTypes[] {
    // When editing existing citations, allow all types regardless of pending status
    if (this.isEdit) {
      return citationTypes;
    }

    // When creating new citations in pending editor, restrict to Plain only
    if (this.isPendingCitation) {
      return ['Plain'] as UserCitationTypes[];
    } else {
      return citationTypes;
    }
  }

  get documentId(): number {
    return +this.$route.params.documentId;
  }

  get projectId(): number {
    return +this.$route.params.projectId;
  }

  @Action('projects/createCitation')
  public createCitation!: (payload: CreateCitationRequestPayload) => Citation;

  @Action('projects/editCitation')
  public editCitation!: (payload: EditCitationRequestPayload) => void;

  @Action('documents/removeRevisionCitation')
  public removeRevisionCitation!: (
    payload: RemoveRevisionCitationRequestPayload
  ) => void;

  @Action('documents/getLatestRevisionCitationsByDocumentId')
  public getLatestRevisionCitationsByDocumentId!: (
    documentId: number
  ) => Citation[];

  public created() {
    // init citation fields
    if (this.isEdit) {
      this.transformCitationToField();
    }
  }

  public async validate() {
    if (this.$refs.observer) {
      const result = await (this.$refs.observer as any).validate();
      return result;
    }
    return false;
  }

  // Close modal on error
  @Watch('projectsApiState.createCitation.error')
  @Watch('projectsApiState.editCitation.error')
  @isDifferent
  @isTruthy
  public onCitationApiErrorResponse() {
    this.$emit('closeModal');
  }

  // Close modal on error and update Text sections
  @Watch('projectsApiState.createCitation.success')
  @Watch('projectsApiState.editCitation.success')
  @Watch('newlyCreatedCitation')
  @isDifferent
  @isTruthy
  public async onCitationApiResponse() {
    if (this.isEdit) {
      // For some reason citation id is string in pending editor
      const citationId: number = isInteger(this.citation.id)
        ? this.citation.id
        : Number(this.citation.id);
      if (
        this.referencedCitationIds.includes(citationId) &&
        this.newlyCreatedCitation
      ) {
        // remove the edited citation
        await this.removeRevisionCitation({
          documentId: this.documentId,
          revisionId: this.pendingDocumentRevisionId,
          citationId: citationId
        });

        //Update dirtyReferences

        if (this.dirtyReferences) {
          const index = this.dirtyReferences.citationIds.indexOf(
            citationId.toString()
          );

          if (index !== -1) {
            this.dirtyReferences.citationIds[
              index
            ] = this.newlyCreatedCitation.id.toString();
          }
        }

        //Update dirtyTextSections

        this.dirtyTextSections = this.dirtyTextSections.map((textSection) => {
          if (this.newlyCreatedCitation) {
            textSection.content.sectionValue = updateCitationInOpsInStagingEditor(
              {
                ops: textSection.content.sectionValue!,
                citationId: citationId.toString(),
                newCitationId: this.newlyCreatedCitation.id.toString()
              }
            );
          }
          return { ...textSection };
        });

        //update activeCitations and dirtyCitations
        this.activeCitations = this.activeCitations.filter((el) => {
          return el.id !== this.citation.id;
        });

        this.dirtyCitations = this.dirtyCitations.filter((el) => {
          return el.id !== this.citation.id;
        });

        const citationTobePushed = {
          id: this.newlyCreatedCitation.id.toString(),
          content: this.newlyCreatedCitation.content
        };

        this.activeCitations.push(citationTobePushed);
        this.dirtyCitations.push(citationTobePushed);

        //Filter out duplicate citation ids
        this.activeCitations = this.activeCitations.filter((el) => {
          return !isInteger(el.id);
        });
        this.dirtyCitations = this.dirtyCitations.filter((el) => {
          return !isInteger(el.id);
        });
      }
    }

    const toastMessage: string = `Citation Edited Successfully`;
    Toast.open({
      position: 'is-top',
      type: 'is-dark',
      message: toastMessage
    });

    // Fetch all latest revision citations
    this.getLatestRevisionCitationsByDocumentId(this.documentId);
    this.$emit('closeModal');
  }

  // Generate fields on citation type change
  @Watch('selectedType', { immediate: true })
  @isDifferent
  @isTruthy
  public onSelectedTypeChanged(type: UserCitationTypes) {
    const oldFormFields = this.formFields;
    const newFormFields = _cloneDeep(allFormFields.types[type]);
    this.formFields = newFormFields.map((formField) => {
      const oldField = oldFormFields.find(
        ({ label }) => label === formField.label
      );
      // Keep value when type change
      return oldField
        ? {
            ...formField,
            value: oldField.value
          }
        : formField;
    });
  }

  get isEdit(): boolean {
    return !!this.citation;
  }

  get citationPreview(): string {
    if (!this.formFields.length) {
      return '';
    }
    if (this.selectedType === 'Plain') {
      return _get(
        this.formFields.find((obj) => obj.name === 'plainText'),
        'value',
        ''
      );
    }

    const authorField = this.formFields.find(
      (obj) => obj.name === FORM_FIELD_NAMES.AUTHOR
    );
    let authorString = '';

    if (authorField && authorField.value) {
      try {
        // Parse the JSON author data
        const authors = JSON.parse(authorField.value);
        if (Array.isArray(authors)) {
          authorString = authors
            .map((author: any) => {
              if (author.type === AUTHOR_TYPES.INDIVIDUAL) {
                const given = author.given || '';
                const family = author.family || '';
                return `${given} ${family}`.trim();
              } else if (author.type === AUTHOR_TYPES.ORGANISATION) {
                return author.literal || '';
              }
              return '';
            })
            .filter((name) => name.length > 0)
            .join(', ');
        }
      } catch (e) {
        // Fallback to raw value if JSON parsing fails
        authorString = authorField.value;
      }
    }

    const title = _get(
      this.formFields.find((obj) => obj.name === FORM_FIELD_NAMES.TITLE),
      'value',
      ''
    );

    if (!authorString && !title) {
      return '';
    }

    return authorString ? `${authorString} . ${title}` : title;
  }

  public async handleCitationFormSubmit() {
    const citation = this.transformFieldToCitation();

    // If pending editor or not cited, edit citation directly
    if (this.isEdit) {
      // For some reason citation id is string in pending editor
      const citationId: number = isInteger(this.citation.id)
        ? this.citation.id
        : Number(this.citation.id);

      if (!this.referencedCitationIds.includes(citationId)) {
        const editCitation = await this.editCitation({
          projectId: this.projectId,
          citation
        });

        // Fetch all latest revision citations
        this.getLatestRevisionCitationsByDocumentId(this.documentId);
        this.$emit('closeModal');
        return editCitation;
      }
    }

    const newCitation = await this.createCitation({
      projectId: this.projectId,
      citation,
      revisionId: this.pendingDocumentRevisionId
    });

    return newCitation;
  }

  public transformFieldToCitation() {
    let citationObject: {
      type: CitationTypes | null;
      author?: CitationAuthor[];
      [key: string]: any;
    } = {
      type: this.transformCitationFormTypeToCitationCSLType(
        this.selectedType as string
      )
    };

    if (this.isEdit) {
      citationObject = { ...citationObject, ...{ id: this.citation.id } };
    }

    this.formFields.forEach((obj) => {
      if (obj.name === FORM_FIELD_NAMES.AUTHOR) {
        try {
          // Try to parse as JSON first (modern author handling)
          const parsedAuthors = JSON.parse(obj.value);
          citationObject = {
            ...citationObject,
            ...{ [obj.name]: parsedAuthors }
          };
        } catch (e) {
          // Fallback to string parsing for legacy support
          citationObject = {
            ...citationObject,
            ...{
              [obj.name]: this.transformAuthorStringToAuthorArray(obj.value)
            }
          };
        }
      } else {
        citationObject = { ...citationObject, ...{ [obj.name]: obj.value } };
      }
    });

    // Clean up author format similar to document editor
    if (FORM_FIELD_NAMES.AUTHOR in citationObject) {
      const cslAuthors = (citationObject.author as CitationAuthorFormItem[]).map(
        ({ id, type, ...author }) => {
          if (type === AUTHOR_TYPES.INDIVIDUAL) {
            const { given, family } = author as IndividualCitationAuthor;
            return { given, family } as CitationAuthorFormItem;
          } else if (type === AUTHOR_TYPES.ORGANISATION) {
            const { literal } = author as OrganisationCitationAuthor;
            return { literal } as CitationAuthorFormItem;
          }
          return author as CitationAuthorFormItem;
        }
      );
      citationObject.author = cslAuthors;
    }

    return citationObject;
  }

  public transformCitationToField() {
    if (!this.isEdit) {
      return [];
    }

    const citation = _cloneDeep(this.citation.content);

    const transformedType = this.transformCitationCSLTypeToCitationFormType(
      citation.type
    );

    this.selectedType = transformedType;

    if (!this.selectedType) {
      this.selectedType = 'Journal Article';
    }

    this.formFields = _cloneDeep(
      allFormFields.types[this.selectedType as UserCitationTypes]
    );

    this.formFields.forEach((field: FormFieldsProperties) => {
      const fieldValue = citation[field.name];

      // Handle author field specially - convert to proper format
      if (field.name === FORM_FIELD_NAMES.AUTHOR) {
        if (Array.isArray(fieldValue)) {
          // Author is already an array - update the authors array and JSON string
          const updatedAuthors = fieldValue.map(
            (author: any, index: number) => ({
              id: author.id || Date.now() + index,
              given: author.given || '',
              family: author.family || '',
              literal: author.literal || '',
              type: author.literal
                ? AUTHOR_TYPES.ORGANISATION
                : AUTHOR_TYPES.INDIVIDUAL
            })
          );
          // Use Vue's reactivity system to ensure proper updates
          this.$set(this, 'authors', updatedAuthors);
          field.value = JSON.stringify(updatedAuthors);
        } else if (typeof fieldValue === 'string' && fieldValue.trim()) {
          try {
            // Try to parse as JSON in case it's a stringified array
            const parsedAuthors = JSON.parse(fieldValue);
            if (Array.isArray(parsedAuthors)) {
              const updatedAuthors = parsedAuthors.map(
                (author: any, index: number) => ({
                  id: author.id || Date.now() + index,
                  given: author.given || '',
                  family: author.family || '',
                  literal: author.literal || '',
                  type: author.literal
                    ? AUTHOR_TYPES.ORGANISATION
                    : AUTHOR_TYPES.INDIVIDUAL
                })
              );
              // Use Vue's reactivity system to ensure proper updates
              this.$set(this, 'authors', updatedAuthors);
              field.value = JSON.stringify(updatedAuthors);
            } else {
              // It's a regular string, convert to authors array
              const authorsArray = this.transformAuthorStringToAuthorArray(
                fieldValue
              );
              const updatedAuthors = authorsArray.map(
                (author: any, index: number) => ({
                  id: Date.now() + index,
                  given: author.given || '',
                  family: author.family || '',
                  literal: '',
                  type: AUTHOR_TYPES.INDIVIDUAL
                })
              );
              // Use Vue's reactivity system to ensure proper updates
              this.$set(this, 'authors', updatedAuthors);
              field.value = JSON.stringify(updatedAuthors);
            }
          } catch (e) {
            // Not valid JSON, treat as regular string
            const authorsArray = this.transformAuthorStringToAuthorArray(
              fieldValue
            );
            const updatedAuthors = authorsArray.map(
              (author: any, index: number) => ({
                id: Date.now() + index,
                given: author.given || '',
                family: author.family || '',
                literal: '',
                type: AUTHOR_TYPES.INDIVIDUAL
              })
            );
            // Use Vue's reactivity system to ensure proper updates
            this.$set(this, 'authors', updatedAuthors);
            field.value = JSON.stringify(updatedAuthors);
          }
        } else {
          const defaultAuthors = [getDefaultAuthor()];
          // Use Vue's reactivity system to ensure proper updates
          this.$set(this, 'authors', defaultAuthors);
          field.value = JSON.stringify(defaultAuthors);
        }
      } else {
        field.value = fieldValue || '';
      }
    });
  }

  public transformCitationFormTypeToCitationCSLType(selectedType: string) {
    if (selectedType === 'Book') {
      return CitationTypes.BOOK;
    } else if (selectedType === 'Generic') {
      return CitationTypes.GENERIC;
    } else if (selectedType === 'Book Section') {
      return CitationTypes.BOOK_SECTION;
    } else if (selectedType === 'Conference Paper') {
      return CitationTypes.CONFERENCE_PAPER;
    } else if (selectedType === 'Journal Article') {
      return CitationTypes.JOURNAL_ARTICLE;
    } else if (selectedType === 'Report') {
      return CitationTypes.REPORT;
    } else if (selectedType === 'WebPage') {
      return CitationTypes.WEBPAGE;
    } else if (selectedType === 'Plain') {
      return CitationTypes.PLAIN;
    }
    return null;
  }

  public transformCitationCSLTypeToCitationFormType(selectedType: string) {
    if (selectedType === CitationTypes.BOOK) {
      return 'Book';
    } else if (selectedType === CitationTypes.GENERIC) {
      return 'Generic';
    } else if (selectedType === CitationTypes.BOOK_SECTION) {
      return 'Book Section';
    } else if (selectedType === CitationTypes.CONFERENCE_PAPER) {
      return 'Conference Paper';
    } else if (selectedType === CitationTypes.JOURNAL_ARTICLE) {
      return 'Journal Article';
    } else if (selectedType === CitationTypes.REPORT) {
      return 'Report';
    } else if (selectedType === CitationTypes.WEBPAGE) {
      return 'WebPage';
    } else if (selectedType === CitationTypes.PLAIN) {
      return 'Plain';
    }
    return null;
  }

  public transformAuthorStringToAuthorArray(authorsString: string) {
    const authorsArray = authorsString.split(',');
    return authorsArray.map((author) => {
      const authorArray = author.trim().split(' ');

      if (authorArray.length === 1) {
        // Single word - treat as first name, not last name
        return {
          given: authorArray[0],
          family: ''
        };
      } else {
        // Multiple words - last word is family name, rest are given names
        const lastName: string = authorArray[authorArray.length - 1];
        let firstName: string = '';
        for (let i = 0; i < authorArray.length - 1; i++) {
          firstName =
            firstName === ''
              ? authorArray[i]
              : firstName + ' ' + authorArray[i];
        }
        return {
          given: firstName,
          family: lastName
        };
      }
    });
  }
}
